import { ChevronRight, Loader2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'

export type ConnectionState = 'connected' | 'disconnected' | 'syncing'

interface ConnectionStatusChipProps {
  title: string
  state: ConnectionState
  /** For connected state: when data was last updated */
  lastUpdated?: string | Date
  onClick?: () => void
  className?: string
}

const stateStyles: Record<ConnectionState, string> = {
  connected: 'bg-success-50 text-success-600',
  syncing: 'bg-success-50 text-success-600',
  disconnected: 'bg-warning-50 text-warning-600',
}

function detailText(state: ConnectionState, lastUpdated?: string | Date) {
  if (state === 'syncing') return 'Updating…'
  if (state === 'disconnected') return 'Tap to connect'
  if (lastUpdated)
    return `Updated ${formatDistanceToNow(new Date(lastUpdated), { addSuffix: true })}`
  return 'Connected'
}

export function ConnectionStatusChip({
  title,
  state,
  lastUpdated,
  onClick,
  className,
}: ConnectionStatusChipProps) {
  const Wrapper = onClick ? 'button' : 'div'
  return (
    <Wrapper
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'inline-flex items-center gap-2.5 rounded-full px-3.5 py-2 text-left',
        stateStyles[state],
        onClick && 'hearth-press',
        className
      )}
    >
      {state === 'syncing' ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <span
          className={cn(
            'h-2 w-2 rounded-full',
            state === 'connected' ? 'bg-success-500' : 'bg-warning-500'
          )}
        />
      )}
      <span className="flex flex-col">
        <span className="text-[13px] font-medium">{title}</span>
        <span className="text-[11px] opacity-80">{detailText(state, lastUpdated)}</span>
      </span>
      {onClick && <ChevronRight className="h-4 w-4 opacity-60" />}
    </Wrapper>
  )
}
